import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { FavoritesControllerClient } from '../../api/ph-web/favorites/favorites.apiServiceClientPb';
import {
  AddFavoritesRequest,
  DeleteFavoritesResponse,
  GetFavoritesRequest,
  GetFavoritesResponse,
  DeleteFavoritesRequest,
  StatusInfo,
  AddFavoritesResponse,
  AddressRequest,
  EditFavoritesResponse,
  GetFavoriteRequest,
  GetFavoriteResponse,
  EditFavoritesRequest,
} from '../../api/ph-web/favorites/favorites.api_pb';
import { AddressGeologicType } from '../../models/interface/addressGeologicType.enum';
import { CityShared } from '../../models/interface/cityShared.interface';
import { FavoriteAddressShared } from '../../models/interface/favoriteAddress.interface';
import { FavoriteAddressRequestShared } from '../../models/interface/favoriteAddressRequest.interface';
import { FavoriteAddressResponseShared } from '../../models/interface/favoriteAddressResponse.interface';
import { GrpcFactoryService } from '../../sharedServices/grpc-factory.service';
import { MuException } from '../../types/exception.type';

@Injectable({
  providedIn: 'root',
})
export class MuFavoriteService {
  private favoriteClient = this.grpcFactoryService.create(
    FavoritesControllerClient,
    `${environment.hostname}${environment.hostPort}`,
    'FavoritesControllerClient'
  );

  private getFavoriteAddressListCall = this.grpcFactoryService.call(this.favoriteClient, this.favoriteClient.getFavorites, {
    callName: 'getFavorites',
  });

  private deleteFavoriteAddressCall = this.grpcFactoryService.call(this.favoriteClient, this.favoriteClient.deleteFavorites, {
    callName: 'deleteFavorites',
  });

  private createFavoriteCall = this.grpcFactoryService.call(this.favoriteClient, this.favoriteClient.addFavorites, {
    callName: 'addFavorites',
  });

  private editFavoriteCall = this.grpcFactoryService.call(this.favoriteClient, this.favoriteClient.editFavorites, {
    callName: 'editFavorites',
  });

  private getFavoriteCall = this.grpcFactoryService.call(this.favoriteClient, this.favoriteClient.getFavorite, {
    callName: 'getFavorite',
  });

  constructor(private grpcFactoryService: GrpcFactoryService) {}

  /**
   * Return the other value for addressType {'home': 'CASA'} depends on entry
   * @param type
   */
  private addressTypeSynonym(type: string): string {
    const addressType = {
      home: 'CASA',
      office: 'OFICINA',
      other: 'OTRO',
    };
    return (
      Object.entries(addressType)
        .filter(item => item.includes(type))
        .map(item => (item[0] === type ? item[1] : item[0]))[0] || ''
    );
  }

  /**
   * Return the address object for saving requests (add and edit)
   * @param data
   * @returns
   */
  private returnAddressObject(data: FavoriteAddressShared): AddressRequest {
    const address: AddressRequest = new AddressRequest();

    address.setCityid(data.cityId);
    address.setAddress(data.address);
    address.setDescription(data.description);
    address.setAlias(data.alias);
    address.setAddresstype(this.addressTypeSynonym(data.addressType));
    address.setAddresscomplement(data.addressComplement);
    address.setCountry(data.country);

    return address;
  }

  /**
   * create favorite address
   * @param {FavoriteAddressShared} data
   */
  async createFavorite(data: FavoriteAddressShared): Promise<void> {
    const request: AddFavoritesRequest = new AddFavoritesRequest();
    request.setUserid(data.userId);
    request.setAddress(this.returnAddressObject(data));

    const response: AddFavoritesResponse = await this.createFavoriteCall(request);

    if (!response || response?.getStatus()?.getErrorcode() !== StatusInfo.ErrorCode.OK) {
      throw new MuException({
        key: 'common',
        message: response?.getStatus()?.getMessage(),
      });
    }
  }

  /**
   * Update an address
   * @param data
   */
  async editFavorite(data: FavoriteAddressShared): Promise<void> {
    const request: EditFavoritesRequest = new EditFavoritesRequest();
    const address: AddressRequest = this.returnAddressObject(data);
    request.setId(data.id);
    request.setAddress(address);

    const response: EditFavoritesResponse = await this.editFavoriteCall(request);

    if (!response || response?.getStatus()?.getErrorcode() !== StatusInfo.ErrorCode.OK) {
      throw new MuException({
        key: 'common',
        message: response?.getStatus()?.getMessage(),
      });
    }
  }

  /**
   * Get favorite addressList
   * @param favoriteRequest
   * @returns
   */
  async getFavoriteAddressList(
    favoriteRequest: FavoriteAddressRequestShared,
    cityList?: CityShared[]
  ): Promise<FavoriteAddressResponseShared> {
    const request = new GetFavoritesRequest();
    request.setIduser(parseInt(favoriteRequest.userId, 10));
    request.setLimit(favoriteRequest.limit);
    request.setPage(favoriteRequest.page);

    const response: GetFavoritesResponse = await this.getFavoriteAddressListCall(request);

    const addressList: FavoriteAddressShared[] = response.getFavoritesList().map(address => {
      const city: CityShared = cityList?.find(
        x =>
          x.name?.toLocaleLowerCase()?.trim() === address.getCity()?.toLowerCase()?.trim() ||
          x.geoName?.toLowerCase()?.trim() === address?.getCity()?.trim()
      );
      return {
        id: address.getId(),
        userId: address.getIduser(),
        address: address.getAddress(),
        geoLocation: {
          latitude: address.getGeolocation()?.getLat(),
          longitude: address.getGeolocation()?.getLng(),
        },
        cityName: city ? city.name : '',
        cityId: city ? city.id : address.getCityid(),
        city: city ? city.geoName : address.getCity(),
        description: address.getDescription(),
        addressComplement: address.getAddresscomplement(),
        alias: this.removeDate(address.getAlias()),
        type: AddressGeologicType.FAVORITE_OTHER,
        text: `locationType.${AddressGeologicType.FAVORITE_OTHER}`,
        iconClass: 'mu-icon mu-icon-star',
      };
    });

    return {
      values: addressList,
      page: response.getProperties().getCurrentpage(),
      limit: response.getProperties().getLimit(),
      totalPages: response.getProperties().getTotalpages(),
      totalRows: response.getProperties().getTotalrows(),
    };
  }

  /**
   * Delete a favorite address
   * @param {FavoriteAddressShared} favoriteAddress
   */
  async deleteFavoriteAddress(favoriteAddress: FavoriteAddressShared): Promise<void> {
    const request = new DeleteFavoritesRequest();
    request.setId(favoriteAddress.id);

    const response: DeleteFavoritesResponse = await this.deleteFavoriteAddressCall(request);

    if (response.getStatus().getStatus() !== StatusInfo.StatusType.SUCCESS) {
      const message = response.getStatus().getMessage();
      throw new MuException({ message });
    }
  }

  /**
   * get favorite address by id
   * @param id
   * @returns
   */
  async getFavoriteAddress(id: number, idUser: number): Promise<FavoriteAddressShared> {
    const request: GetFavoriteRequest = new GetFavoriteRequest();
    request.setIduser(idUser);
    request.setId(id);

    const address: GetFavoriteResponse = await this.getFavoriteCall(request);

    return {
      id: address.getFavorite().getId(),
      cityId: address.getFavorite().getCityid(),
      address: address.getFavorite().getAddress(),
      addressComplement: address.getFavorite().getAddresscomplement(),
      alias: this.addressTypeSynonym(address.getFavorite().getAddresstype()),
      aliasName: this.removeDate(address.getFavorite().getAlias()),
    };
  }

  /**
   * remove word date
   * @param {string} dateAlias
   * @returns {string}
   */
  removeDate(dateAlias: string): string {
    let alias = dateAlias;
    if (alias.length >= 24) {
      const tmp = alias.split('__');
      alias = tmp[0];
    }

    return alias;
  }
}
